import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Space,
  Text,
} from '@tonic-one/react';
import { useTranslation } from 'react-i18next';

import { QuarantineApi } from '@Apis';
import { QuarantineLogTypeEnum } from '../../../types/log';
import { QUARANTINE_TYPE_NAME_MAP } from '../utils/constants';
import { QuarantineRestoreActionReqDto } from '../utils/types';
import { QuarantineItemModalName, QuarantineItemModalState } from './interface';
import { getQuarantineRestoreActionURL } from '../utils/fns';
import LoadingBtn from '../../../components/Common/LoadingBtn';
import { RestoreAllModal } from './restoreAll';

export function RestoreModal({
  quarantineType,
  modal,
  setModal,
  setTrigger,
  selectAllForRestore,
  total,
}: {
  quarantineType: QuarantineLogTypeEnum;
  modal: QuarantineItemModalState;
  setModal: ({ name, quarantineItems }: QuarantineItemModalState) => void;
  setTrigger: () => void;
  selectAllForRestore: boolean;
  total: number;
}) {
  const { t } = useTranslation();
  const onClose = () => {
    setModal({
      name: QuarantineItemModalName.None,
      quarantineItems: modal.quarantineItems,
    });
  };

  const onClick = async () => {
    if (selectAllForRestore) {
      return;
    } else {
      const data: QuarantineRestoreActionReqDto = {
        itemList: [],
      };

      const url = getQuarantineRestoreActionURL(quarantineType);
      if (url === 'spod/restore') {
        modal.quarantineItems.forEach((_item) => {
          data.itemList.push({
            uniqueId: _item.uniqueId || '',
            uniqueLogId: _item.uniqueLogId,
            quarantineType: _item.quarantineTypeKey,
            traceId: _item.traceId,
            index: _item.index,
            type: _item.type,
            id: _item.id,
            dedicatedSite: _item.dedicatedSite,
            violator: _item.violator,
            companyGuid: _item.companyGuid,

            siteUrl: _item.siteUrl,
            graphSiteId: _item.graphSiteId,
            useSpodtmDelegator: _item.useSpodtmDelegator || 'false',

            originalDriveId: _item.originalDriveId || '',
            originalParentId: _item.originalParentId || '',
            originalId: _item.originalId || '',
            fileName: (_item as any).fileNamePath[_item.uniqueId],
            fileOriginalPath: _item.fileOriginalPath || '',

            quarantineDriveId: _item.quarantineDriveId || '',
            quarantineParentId: _item.quarantineParentId || '',
            quarantineItemId: _item.quarantineItemId || '',
            quarantineFileName: _item.quarantineFileName || '',
            notificationFileId: _item.notificationFileId || '',
          });
        });
        data.serviceName = QUARANTINE_TYPE_NAME_MAP.get(
          quarantineType
        ) as string;
      } else if (url === 'exchangeOnline/restore') {
        modal.quarantineItems.forEach((_item) => {
          data.itemList.push({
            msgReceivedTime: _item.msgReceivedTime,
            uniqueId: _item.uniqueId,
            uniqueLogId: _item.uniqueLogId,
            emailAddress: _item.violator,
            traceId: _item.traceId,
            index: _item.index,
            type: _item.type,
            id: _item.id,
            violator: _item.violator,
            companyGuid: _item.companyGuid,
            protectionMode: _item.protectionMode,
            msgFrom: _item.msgFrom,
            msgTo: _item.msgTo,
            msgSubject: _item.msgSubject,
            messageId: _item.messageId,
            folder: _item.folderPath,
          });
        });
      } else if (url === 'inlineMail/restore') {
        modal.quarantineItems.forEach((_item) => {
          data.itemList.push({
            companyGuid: _item.companyGuid,
            esLogIndex: _item.index,
            esLogId: _item.id,
            esLogType: _item.type,
            uniqueLogId: _item.uniqueLogId,
            emsTraceId: _item.emsTraceId,
            uniqueId: _item.uniqueId,
            traceId: _item.traceId,
            emailAddress: _item.violator,
            recipients: _item.msgTo,
            originalRecipients: _item.originalMsgTo,
            backupBlobPath: _item.backupBlobPath ? _item.backupBlobPath : '',
            restoreBy: '',
            direction: _item.mailDirectionKey,
            blobPath: _item.blobPath ? _item.blobPath : '',
            msgFrom: _item.msgFrom,
            msgSubject: _item.msgSubject,
            messageId: _item.messageId,
            folder: _item.folderPath,
          });
        });
        data.serviceName = QUARANTINE_TYPE_NAME_MAP.get(
          quarantineType
        ) as string;
      } else if (url === 'cloudApp/restore') {
        modal.quarantineItems.forEach((_item) => {
          data.itemList.push({
            logTimestamp: _item.timestampKey,
            uniqueLogId: _item.uniqueLogId,
            uniqueFileId: _item.uniqueId,
            violatorId: _item.violatorId,
            folderId: _item.folderId,
            fileName: _item.fileName,
            resourceId: _item.resourceId,
            isShared: _item.isShared,
            shareSetting: _item.shareSetting,
            violator: _item.violator,
            companyGuid: _item.companyGuid,
            rootNodeId: _item.rootNodeId,
          });
        });
        data.serviceName = QUARANTINE_TYPE_NAME_MAP.get(
          quarantineType
        ) as string;
      } else if (url === 'teams/restore') {
        modal.quarantineItems.forEach((_item) => {
          data.itemList.push({
            uniqueId: _item.uniqueId || '',
            uniqueLogId: _item.uniqueLogId,
            quarantineType: _item.quarantineTypeKey,
            traceId: _item.traceId,
            index: _item.index,
            type: _item.type,
            id: _item.id,
            violator: _item.violator,
            companyGuid: _item.companyGuid,

            siteUrl: _item.siteUrl,
            graphSiteId: _item.graphSiteId,
            useSpodtmDelegator: _item.useSpodtmDelegator || 'false',

            originalDriveId: _item.originalDriveId || '',
            originalParentId: _item.originalParentId || '',
            originalId: _item.originalId || '',
            fileName: _item.fileName,
            fileOriginalPath: _item.fileOriginalPath || '',

            quarantineDriveId: _item.quarantineDriveId || '',
            quarantineParentId: _item.quarantineParentId || '',
            quarantineItemId: _item.quarantineItemId || '',
            quarantineFileName: _item.quarantineFileName || '',
            notificationFileId: _item.notificationFileId || '',
          });
        });
      } else if (url === 'exchangeServer/restore') {
        modal.quarantineItems.forEach((_item) => {
          data.itemList.push({
            uniqueId: _item.uniqueId,
            messageId: _item.messageId,
            uniqueLogId: _item.uniqueLogId,
            smexServerFqdn: _item.productServerName,
            index: _item.index,
            type: _item.type,
            id: _item.id,
            companyGuid: _item.companyGuid,
            violator: _item.productServerName,
          });
        });
      } else if (url === 'gmail/restore') {
        modal.quarantineItems.forEach((_item) => {
          data.itemList.push({
            logDocId: _item.id,
            logId: _item.uniqueLogId,
            logIndexId: _item.index,
            logType: _item.type,
            uniqueId: _item.uniqueId,
            violator: _item.violator,
            messageId: _item.messageId,
            internetMessageId: _item.internetMessageId,
            folder: _item.folderPath,
            msgFrom: _item.msgFrom,
            msgTo: _item.msgTo,
            msgSubject: _item.msgSubject,
            companyGuid: _item.companyGuid,
            dedicatedMailbox: _item.msgDedicatedQuarantineMailbox,
          });
        });
      }
      try {
        await QuarantineApi.apiRestoreQuarantineLogs(url, data);
        window.showNotification({
          titleMsg: t('quarantine.modal.action.successInfo'),
          severity: 'success',
        });
        setModal({
          name: QuarantineItemModalName.None,
          quarantineItems: [],
        });
        setTrigger();
      } catch {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
        onClose();
      }
    }
  };

  if (modal.name !== QuarantineItemModalName.Restore) {
    return <></>;
  }

  if (selectAllForRestore) {
    return (
      <RestoreAllModal
        total={total}
        modal={modal}
        setModal={setModal}
        setTrigger={setTrigger}
      />
    );
  }

  return (
    <Modal
      closeOnEsc
      closeOnOutsideClick
      isClosable
      isOpen={modal.name === QuarantineItemModalName.Restore}
      onClose={onClose}
      size="sm"
    >
      <ModalOverlay />
      <ModalContent width={480}>
        <ModalBody>
          {selectAllForRestore ? (
            <Text>{t('quarantine.restoreAll.confirmStart', { total })}</Text>
          ) : (
            <Text mb="4x">{t('quarantine.modal.restore.desc')}</Text>
          )}
        </ModalBody>
        <ModalFooter>
          <LoadingBtn variant="primary" minWidth="20x" onClick={onClick}>
            {t('common.button.ok')}
          </LoadingBtn>
          <Space width="2x" />
          <Button minWidth="20x" onClick={onClose}>
            {t('common.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
